/**
 * 請求書の紙（縦串5、024、docs/19）。受注した見積から起こした下書きを直して、出して、PDF に。
 *   ・行・内訳は見積のエディタ（Estimate.tsx の Editor、invoice モード）で書く。見積から写した明細は直せる（射場さん 9/16）
 *   ・紙の上（件名・請求日・お支払期限・工事場所・工期）と備考はここで書き、行とは別に保存する（PUT の省いた欄は変わらない）
 *   ・お支払期限は顧客の支払条件から計算した値が入っている。請求書ごとに直せる。空だと出せない
 *   ・出す（I-0001）：初回は会社名を聞く。登録番号があれば適格請求書。取消しは見積と同じ
 *   ・振込先（043、docs/32）：起こした時点の既定の口座が入る。口座が2つ以上なら下書きのあいだ紙の上で選び直せる。出した時点で固まる
 *   ・紙の上の自動保存は共通の仕組み（#177、lib/saveQueue.tsx）：重ねて送らない・保存できていなければ 発行・PDF に進まない・離れるときに確かめる
 */
import { BUNDLE_MODE_LABEL, CLOSING_MSG, IMPORT_MSG, NUMBERING_MSG, encodeTitleHeader, RECURRING_MSG, ESTIMATE_PC_MSG, INVOICE_MSG, TAX_MSG, UNISSUE_AFTER_PAYMENT_VOID, bankAccountText, closingDayLabel, closingPeriodLabel, saveGateText, type BankAccountsPage, type Company, type InstallmentChoice, type Invoice, type IssueResult, type LineInput, type SheetInput, type UnissueResult, UNISSUED_MSG, unissuedReissue, unissuedTitle, headRecipientOf } from "@genbaryoku/contract";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useRef, useState, type MouseEvent } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router";
import { api, apiUpload, ApiError, messageOf } from "../api.js";
import { EditingChip, useEditors } from "../components/Editing.jsx";
import { namePageNow } from "../components/BackButton.jsx";
import { docShort } from "../components/AreaIcon.jsx";
import { ME_KEY, useMe } from "../auth.js";
import { DocField, subjWidth } from "../components/DocHead.jsx";
import { DateInput } from "../components/DateInput.jsx";
import { UnissueDialog } from "../components/UnissueDialog.jsx";
import { PhoneFields } from "../components/PhoneFields.jsx";
import { Icon } from "../components/Shell.jsx";
import { ErrorText, Field, Help, useToast } from "../components/ui.jsx";
import { t, useLabels } from "../labels.js";
import { dayJst, md, mdSlash, yen } from "../lib/num.js";
import { useSaveQueue } from "../lib/saveQueue.jsx";
import { usePcOnly } from "../lib/pcOnly.js";
import { Editor, helpText, type InvoiceBackend } from "./Estimate.jsx";
import { useCreateInvoice } from "./Invoices.jsx";
import { Slot, slotActions } from "../core/slots.jsx";
import { HeadActions, type HeadAction } from "../components/HeadActions.jsx";
import { DeleteDraftDialog } from "../components/DeleteDraftDialog.jsx";
import { DeliveriesLink } from "../components/Deliveries.jsx";

type Head = { subject: string; issueDate: string; dueDate: string; siteAddress: string; workPeriodText: string; notes: string; bankAccountId: string | null; taxPointDate: string | null };
const fromServer = (i: Invoice): Head => ({
  subject: i.revision.subject,
  issueDate: i.revision.issueDate,
  dueDate: i.revision.dueDate ?? "",
  siteAddress: i.revision.siteAddress,
  workPeriodText: i.revision.workPeriodText,
  notes: i.revision.notes,
  bankAccountId: i.bank.id,
  // T2：帳票の取引日（空＝請求日で計算）。欄は taxDateHint のときだけ
  taxPointDate: i.revision.taxPointDate ?? null,
});

export function InvoicePage() {
  const { id = "" } = useParams();
  const q = useQuery({ queryKey: ["invoice", id], queryFn: () => api<Invoice>("GET", `/invoices/${id}`), retry: false });
  if (q.isPending) return <p className="hint">読み込んでいます…</p>;
  if (q.isError) {
    return (
      <div className="soonbox">
        <ErrorText>{messageOf(q.error)}</ErrorText>
        <p>
          <Link className="btn text" to="/invoices">請求書一覧へ</Link>
        </p>
      </div>
    );
  }
  return <InvoiceEditor key={q.data.id + q.data.status} initial={q.data} />;
}

function InvoiceEditor({ initial }: { initial: Invoice }) {
  const nav = useNavigate();
  const pcOnly = usePcOnly(); // 発行のあと紙の画面へ移るのは PC だけ（スマホは編集画面に残る。PC_ONLY_FEATURES の mailSend と同じ判定）
  const loc = useLocation();
  const qc = useQueryClient();
  const me = useMe();
  const { toast, node: toastNode } = useToast();
  const createInvoice = useCreateInvoice(toast); // 042：次の回の請求書をここから起こす
  const L = useLabels(); // 会社の言葉（027）
  // 宛名の敬称のところ（039）：顧客担当者がいれば「部署 氏名 様」（御中は付けない）。出したあとは凍結した宛名
  const contactText = initial.issued ? initial.issued.recipientContact : initial.client.contact ? `${initial.client.contact.department} ${initial.client.contact.name}`.trim() : null;
  const toWhom = contactText ? `${contactText} 様` : initial.client.honorific;
  /** #315：頭の宛先（画面は「略称 ／ 支店 ／ 担当者」・乗せると紙の宛名）。出したあとは固めた担当者の名前 */
  const rcp = headRecipientOf({ name: initial.client.name, shortName: initial.client.shortName ?? "", honorific: initial.client.honorific, branch: initial.client.branch?.name ?? null, contact: contactText });
  /** いまの振込先の1行（保存のたびに API が返す。出したあとは固めた値） */
  const bankNow = (qc.getQueryData(["invoice", initial.id]) as Invoice | undefined)?.bank.text ?? initial.bank.text;
  /** 支払条件をどこで決めたか（039：案件で変えていれば案件） */
  const termsOwner = initial.paymentTermsSource === "group" ? L.project : L.customer;
  /** 分割請求の回（042、docs/30）。紙の上に「ご契約金額／これまでのご請求／今回のご請求／残り」 */
  const inst = initial.installment;
  const fresh = !!(loc.state as { fresh?: boolean } | null)?.fresh;
  const readOnly = initial.status !== "draft";
  /** 編集中（#202、docs/82 §4）：下書きを開いている間だけ合図を送り、自分以外を見出しのチップに */
  const editors = useEditors(initial.id, !readOnly);
  const [head, setHead] = useState<Head>(() => fromServer(initial));
  const [more, setMore] = useState(() => !!(initial.revision.siteAddress || initial.revision.workPeriodText || initial.managementNo));
  /* 管理番号（#268 1.5b）：社内の目印・紙に出さない・発行のあとでも直せる＝帳票の版と別の口。欄を離れたときに保存 */
  const [mgmt, setMgmt] = useState(initial.managementNo ?? "");
  const mgmtSaved = useRef(initial.managementNo ?? "");
  const attachOriginal = async (input: HTMLInputElement) => {
    const f = input.files?.[0];
    input.value = "";
    if (!f) return;
    try {
      await apiUpload(`/invoices/${initial.id}/original-pdf`, f, "application/pdf", { "x-gb-title": encodeTitleHeader(f.name) });
      await qc.invalidateQueries({ queryKey: ["invoice", initial.id] });
      toast(IMPORT_MSG.pdfAttached, undefined, "success", "invoice-original-pdf-saved");
    } catch (e) {
      toast(messageOf(e), undefined, "error");
    }
  };
  const saveMgmt = async () => {
    const v = mgmt.trim();
    if (v === mgmtSaved.current) return;
    if (v.length > 50) {
      toast(NUMBERING_MSG.managementTooLong, undefined, "error");
      return;
    }
    try {
      const r = await api<{ managementNo: string | null }>("PUT", `/invoices/${initial.id}/management-no`, { managementNo: v || null });
      mgmtSaved.current = r.managementNo ?? "";
      setMgmt(r.managementNo ?? "");
      qc.setQueryData<Invoice>(["invoice", initial.id], (x) => (x ? { ...x, managementNo: r.managementNo } : x));
      void qc.invalidateQueries({ queryKey: ["invoices"] });
      toast(NUMBERING_MSG.managementSaved, undefined, "success", "management-saved");
    } catch (e) {
      toast(messageOf(e), undefined, "error");
    }
  };
  // 振込口座（043）：下書きのあいだ、口座が2つ以上なら選び直せる
  const accountsQ = useQuery({ queryKey: ["bank-accounts"], queryFn: () => api<BankAccountsPage>("GET", "/bank-accounts"), enabled: !readOnly });
  const accounts = accountsQ.data?.accounts ?? [];
  const admin = me.data?.user.role === "admin";

  /*
   * 版（データが消えうる不具合の直し、2026-10-13）：明細（Editor）と紙の上の欄は、同じ PUT /invoices/:id を別々の列で送る。
   * どちらも「読んだときの版」を送り、ほかのタブ・ほかの人の保存とぶつかったら 409 INVOICE_STALE（前は後から保存した方が明細を全部入れ直して、
   * ほかの直しを黙って消していた）。版は1つを2つの列で使い、送るのは1本ずつ（serial）＝自分どうしでぶつからない
   */
  const ver = useRef(initial.updatedAt);
  const chain = useRef<Promise<unknown>>(Promise.resolve());
  const serial = <T,>(fn: () => Promise<T>): Promise<T> => {
    const p = chain.current.then(fn, fn);
    chain.current = p.catch(() => undefined);
    return p;
  };
  /** ぶつかって読み直しを待つあいだ（Editor の「ほかで変更があります。読み直しますか？」）。紙の上の欄も送らない */
  const staleRef = useRef(false);
  /** Editor の「読み直しますか？」を開く（Editor が登録する） */
  const openStale = useRef<() => void>(() => undefined);
  /** 最後にサーバーと同じだった紙の上の欄（読み直したとき「この画面で直した欄」を見分ける元） */
  const headBase = useRef<Head>(fromServer(initial));

  /* ---- 紙の上の自動保存（行とは別。打ち終わって 0.5 秒後。#177：1本ずつ送る・発行や PDF の前に送り終えるのを待つ・離れるときに確かめる） ---- */
  const latest = useRef(head);
  latest.current = head;
  const hq = useSaveQueue({
    send: async () => {
      const h = latest.current;
      const inv = await serial(() =>
        api<Invoice>("PUT", `/invoices/${initial.id}`, {
          subject: h.subject,
          issueDate: h.issueDate,
          dueDate: h.dueDate || null,
          siteAddress: h.siteAddress,
          workPeriodText: h.workPeriodText,
          notes: h.notes,
          bankAccountId: h.bankAccountId,
          taxPointDate: h.taxPointDate,
          version: ver.current,
        }),
      );
      ver.current = inv.updatedAt;
      headBase.current = fromServer(inv);
      qc.setQueryData(["invoice", initial.id], inv);
      void qc.invalidateQueries({ queryKey: ["invoices"] });
    },
    onError: (err) => {
      if (err instanceof ApiError && err.code === "INVOICE_STALE") {
        staleRef.current = true;
        openStale.current();
        return { msg: ESTIMATE_PC_MSG.staleSaving, then: "stop" };
      }
    },
    blocked: () => staleRef.current,
  });
  /** 紙の上の保存が断られた文（直すまで紙の下に出す） */
  const headErr = hq.state.kind === "error" ? hq.state.msg : undefined;
  const setH = (patch: Partial<Head>, immediate = false) => {
    setHead((h) => ({ ...h, ...patch }));
    latest.current = { ...latest.current, ...patch };
    hq.mark(immediate);
  };
  /** 行の書きかけを送り終えるのを待つ（Editor から受け取る。#177：保存できていなければ理由の文） */
  const linesReady = useRef<() => Promise<string | null>>(async () => null);
  const register = useCallback((f: () => Promise<string | null>) => {
    linesReady.current = f;
  }, []);

  /** 発行・PDF の前：明細と紙の上の書きかけを送り終えるのを待つ。保存できていなければ理由の文（#177） */
  const readyAll = async (): Promise<string | null> => (await linesReady.current()) ?? (await hq.ready());

  /* ---- 出す ---- */
  const needsCompany = !!me.data?.tenant.needsCompanyName;
  const [issueDlg, setIssueDlg] = useState<{ open: boolean; company: { name: string; address: string; phone: string }; error?: string | undefined; busy: boolean; justIssued?: IssueResult | undefined; manualNo?: string | undefined }>({ open: false, company: { name: "", address: "", phone: "" }, busy: false });
  /** 番号の手入力（#268 1.5b）：番号がまだ無く、設定 → 帳票番号 で手入力を許しているときだけ */
  const manualNumber = !initial.documentNo && !!initial.numbering?.manual;
  const [unissue, setUnissue] = useState<{ open: boolean; busy: boolean; error?: string | undefined; done?: string | undefined }>({ open: false, busy: false });
  // 分割払い（#267 3b）：発行の確認の選択（sales の会社だけ差し込み口 invoice.issue が出す）。初めは下書きの上書き → 無ければ{customer}の設定
  const salesOn = !!me.data?.tenant.modules.includes("sales");
  const ownInst = initial.paySchedule?.own ?? null;
  const [instChoice, setInstChoice] = useState<InstallmentChoice>(ownInst == null ? "client" : ownInst);
  const [askDelete, setAskDelete] = useState(false);
  const openIssue = async () => {
    // 保存できていなければ窓を開かずに理由を出す（#177：前は保存に失敗しても発行に進めた）
    const why = await readyAll();
    if (why != null) {
      toast(saveGateText("issue", why), undefined, "error");
      return;
    }
    let company = issueDlg.company;
    try {
      const c = await api<Company>("GET", "/company");
      company = { name: c.name, address: c.address, phone: c.phone };
    } catch {
      /* 空のまま */
    }
    setIssueDlg({ open: true, company, busy: false });
  };
  const doIssue = async () => {
    if (needsCompany && !issueDlg.company.name.trim()) {
      setIssueDlg((d) => ({ ...d, error: INVOICE_MSG.companyRequired }));
      document.getElementById("is_co")?.focus();
      return;
    }
    if (!latest.current.dueDate) {
      setIssueDlg((d) => ({ ...d, error: INVOICE_MSG.dueDateRequired }));
      return;
    }
    setIssueDlg((d) => ({ ...d, busy: true, error: undefined }));
    try {
      const why = await readyAll();
      if (why != null) {
        setIssueDlg((d) => ({ ...d, busy: false, error: saveGateText("issue", why) }));
        return;
      }
      // 会社名を聞くのは初回だけ。振込先は設定の振込口座から入る（043）
      const r = await api<IssueResult>(
        "POST",
        `/invoices/${initial.id}/issue`,
        {
          ...(needsCompany ? { company: { name: issueDlg.company.name.trim(), address: issueDlg.company.address.trim(), phone: issueDlg.company.phone.trim() } } : {}),
          ...(salesOn ? { installments: instChoice } : {}),
          ...(manualNumber && issueDlg.manualNo?.trim() ? { documentNo: issueDlg.manualNo.trim() } : {}),
        },
      );
      setIssueDlg({ open: false, company: issueDlg.company, busy: false, justIssued: r });
      void qc.invalidateQueries({ queryKey: ME_KEY });
      void qc.invalidateQueries({ queryKey: ["invoices"] });
      void qc.invalidateQueries({ queryKey: ["estimates"] });
      void qc.invalidateQueries({ queryKey: ["home"] });
      void qc.invalidateQueries({ queryKey: ["company"] });
      const notice = `${inst ? `${inst.count}回のうち${inst.seq}回目（${inst.label}）の` : ""}請求書ができました。番号 ${r.documentNo} が付きました。`;
      if (!pcOnly.phone) {
        // 2026-10-22：PC は発行が通ったら送信の画面（紙の画面）へ。一言は移った先で出す。「‹ 請求書」で戻れる
        void qc.invalidateQueries({ queryKey: ["invoice", initial.id] });
        namePageNow("請求書", docShort("請求書", r.documentNo)); // 送信の画面の戻るボタンに番号（移る前に。読み直しを待たない）
        nav(pdfUrl, { state: { notice, noticeTestId: "issued-notice" } });
        return;
      }
      await qc.invalidateQueries({ queryKey: ["invoice", initial.id] });
      // 発行の直後の一言は通知（#171。前は題の下の帯）
      toast(notice, undefined, "success", "issued-notice");
      nav(`/invoices/${initial.id}`, { replace: true, state: { justIssued: r.documentNo } });
      window.scrollTo({ top: 0 });
    } catch (e) {
      const err = e instanceof ApiError && e.problem.errors ? (Object.values(e.problem.errors)[0] as string | undefined) : undefined;
      setIssueDlg((d) => ({ ...d, busy: false, error: err ?? messageOf(e) }));
    }
  };
  const doUnissue = async () => {
    setUnissue((u) => ({ ...u, busy: true, error: undefined }));
    try {
      const r = await api<UnissueResult>("POST", `/invoices/${initial.id}/unissue`, {});
      void qc.invalidateQueries({ queryKey: ["invoices"] });
      void qc.invalidateQueries({ queryKey: ["estimates"] });
      void qc.invalidateQueries({ queryKey: ["home"] });
      await qc.invalidateQueries({ queryKey: ["invoice", initial.id] });
      toast(`${r.documentNo} を取り消して下書きに戻しました。`, undefined, "success", "unissued-notice");
      nav(`/invoices/${initial.id}`, { replace: true, state: { unissued: r.documentNo } });
      window.scrollTo({ top: 0 });
    } catch (e) {
      setUnissue((u) => ({ ...u, busy: false, error: messageOf(e) }));
    }
  };
  /**
   * 領収書をつくる（#39）。下書き・出したものがあれば 409 でその id が返るので、そちらをひらく。
   * 入金の記録がある会社（E1、営業力 docs/08 決めごと12。API が payments を返す会社）は「入金も記録する」（既定 ON）
   */
  /** 領収書を起こす。recordPayment＝残の全額を今日・現金で入金として記録する（入金の部品がある会社だけ。#172 で「⋯」の2つの項目に） */
  const createReceipt = async (recordPayment: boolean) => {
    try {
      const r = await api<{ id: string }>("POST", "/receipts", initial.payments ? { fromInvoiceId: initial.id, recordPayment } : { fromInvoiceId: initial.id });
      void qc.invalidateQueries({ queryKey: ["invoice", initial.id] });
      if (initial.payments) {
        void qc.invalidateQueries({ queryKey: ["payments"] });
        void qc.invalidateQueries({ queryKey: ["home"] });
        void qc.invalidateQueries({ queryKey: ["invoices"] });
      }
      nav(`/receipts/${r.id}`, { state: { fresh: true, notice: `請求書 ${initial.documentNo ?? ""} から領収書を作成しました。` } });
    } catch (e) {
      if (e instanceof ApiError && typeof e.problem.receiptId === "string") {
        nav(`/receipts/${e.problem.receiptId}`);
        return;
      }
      toast(messageOf(e), undefined, "error");
    }
  };
  /** 下書き削除（#302 B1：「⋯」の末尾の朱の項目 → 窓 DeleteDraftDialog。断られたら窓の中に API の文） */
  const deleteDraft = async () => {
    await api<void>("DELETE", `/invoices/${initial.id}`);
    hq.discard();
    void qc.invalidateQueries({ queryKey: ["invoices"] });
    void qc.invalidateQueries({ queryKey: ["estimates"] });
    void qc.invalidateQueries({ queryKey: ["home"] });
    nav("/invoices", { replace: true });
  };
  const justIssued = (loc.state as { justIssued?: string } | null)?.justIssued;
  const unissued = (loc.state as { unissued?: string } | null)?.unissued;

  const hasBreakdown = initial.sheets.some((x) => x.type === "breakdown" && x.inUse !== false); // #199：使う内訳があるときだけ紙の形を選ぶ
  const [pdfForm, setPdfForm] = useState<"sheets" | "flat">("sheets");
  const pdfUrl = `/invoices/${initial.id}/paper${hasBreakdown && pdfForm === "flat" ? "?form=flat" : ""}`;
  const openDraftPdf = (ev: MouseEvent<HTMLAnchorElement>) => {
    ev.preventDefault();
    // 書きかけを送り終えてから紙の画面へ。保存できていなければ移らずに理由を出す（#177）
    void readyAll().then((why) => (why == null ? nav(pdfUrl) : toast(saveGateText("pdf", why), undefined, "error")));
  };

  /* 見出しの右の操作（#172、docs/10 §5.12）：1280px でも1段に収める。rank の小さいものが主なボタン、残りは「⋯」。
     取消・分割の予定・入金をつけない領収書は「⋯」の中。入金の操作（入金を記録・入金 n件）は部品 payments が registerActions で足す */
  const nextInst = initial.plan?.next ?? null;
  const balance = initial.payments?.balance ?? 0;
  const invActions: HeadAction[] = readOnly
    ? [
        // 042：分割の次の回はここから起こせる（案件の画面に戻らなくてよい）
        ...(nextInst && nextInst.draftInvoiceId && nextInst.draftInvoiceId !== initial.id
          ? [{ label: `${nextInst.seq}回目下書き表示`, to: `/invoices/${nextInst.draftInvoiceId}`, testId: "next-installment", rank: 20 }]
          : nextInst && !nextInst.draftInvoiceId
            ? [
                {
                  label: `${nextInst.seq}回目請求書作成${nextInst.label ? `（${nextInst.label}）` : ""}`,
                  title: `次は ${nextInst.seq}回目${nextInst.label ? `（${nextInst.label}）` : ""}、${yen(nextInst.total)} 円です`,
                  onSelect: () => void createInvoice(initial.project.quoteId ?? "", nextInst.id),
                  testId: "make-next-installment",
                  rank: 20,
                },
              ]
            : []),
        // 領収書（#39、031）。入金を受けたらこの請求書から起こす。残があるときは「入金も記録する」（今日・残の全額・現金）と「領収書だけ」の2つ
        ...(initial.receipt == null
          ? balance > 0
            ? [
                { label: "領収書作成・入金も記録", title: `今日・残の全額 ${yen(balance)} 円・現金`, onSelect: () => void createReceipt(true), testId: "make-receipt", rank: 30 },
                { label: "領収書だけ作成", title: "入金は記録しません", onSelect: () => void createReceipt(false), testId: "make-receipt-only" },
              ]
            : [{ label: "領収書作成", onSelect: () => void createReceipt(false), testId: "make-receipt", rank: 30 }]
          : [{ label: initial.receipt.status === "draft" ? "領収書下書き表示" : `領収書 ${initial.receipt.documentNo} 表示`, to: `/receipts/${initial.receipt.id}`, testId: "receipt-link", rank: 40 }]),
        ...(initial.plan && initial.projectGroup ? [{ label: "分割予定", to: `/groups/${initial.projectGroup.id}`, testId: "installment-plan" }] : []),
        ...(initial.canUnissue
          ? [{ label: "取り消して下書きに戻す", onSelect: () => setUnissue({ open: true, busy: false }), testId: "unissue-invoice", rank: 90 }]
          : initial.unissueBlockedBy === "payment"
            ? // #173：入金のある請求書は取消を薄く出して押せない。わけは下の1行（API の断り PAYMENT_MSG.unissueHasPayment と同じ文で終わる）
              [{ label: "取り消して下書きに戻す", disabled: true, note: UNISSUE_AFTER_PAYMENT_VOID, testId: "unissue-invoice" }]
            : []),
      ]
    : // #302（B1）：下書きは頭の帯の脇のボタン「PDF 確認」（発行の左）と、「⋯」の末尾の朱の「下書き削除」（見積書と同じ並び）。前は紙の下の行
      [
        { label: ESTIMATE_PC_MSG.pdfCheck, title: INVOICE_MSG.pdfDraft, to: pdfUrl, onLink: openDraftPdf, testId: "pdf-link", rank: 1 },
        { label: ESTIMATE_PC_MSG.discard, danger: true, group: "danger" as const, onSelect: () => setAskDelete(true), testId: "discard-draft" },
      ];
  // PDF の紙の形（#173：前は見出しの「PDF 表示」の左の select。見出しのメニューは「⋯」の1つ＝PDF の段に。#302 で下書きも）
  if (hasBreakdown)
    invActions.push(
      { label: ESTIMATE_PC_MSG.formSheets, checked: pdfForm === "sheets", onSelect: () => setPdfForm("sheets"), testId: "pdf-form-sheets", group: "doc" as const },
      { label: ESTIMATE_PC_MSG.formFlat, checked: pdfForm === "flat", onSelect: () => setPdfForm("flat"), testId: "pdf-form-flat", group: "doc" as const },
    );
  const headActs: HeadAction[] = [...(me.data ? slotActions("invoice.head", me.data, { invoice: initial }) : []), ...invActions];

  /** {deliveryNote}から起こした請求書（#266、docs/96）：載せた{deliveryNote}のチップ。税を{deliveryNote}で確定する{customer}なら明細は直せない */
  const fromNotes = initial.fromDeliveryNotes ?? null;
  const notesChip = fromNotes?.notes.length ? (
    <span className="chip quiet" data-testid="invoice-from-notes" title={fromNotes.notes.map((x) => `${x.documentNo ?? ""}（${mdSlash(x.issueDate)} 納品）`).join("、")}>
      {L.deliveryNote} {fromNotes.notes.length}枚
    </span>
  ) : null;
  const backend: InvoiceBackend = {
    linesReadOnly: !!fromNotes?.taxFromNotes,
    // 明細の保存も同じ版で、紙の上の欄の保存と1本ずつ（上の serial）
    save: (body: { sheets: SheetInput[]; lines: LineInput[] }) =>
      serial(async () => {
        const inv = await api<Invoice>("PUT", `/invoices/${initial.id}`, { ...body, version: ver.current });
        ver.current = inv.updatedAt;
        return inv;
      }),
    version: () => ver.current,
    reload: () => api<Invoice>("GET", `/invoices/${initial.id}`),
    /** ぶつかった（明細の保存・並べ方の保存）：紙の上の欄も送らない（読み直しを待つ） */
    stale: () => {
      staleRef.current = true;
    },
    registerStale: (open) => {
      openStale.current = open;
    },
    /**
     * サーバーの最新に合わせた（読み直した・並べ方を保存した）：版を受け取り、紙の上の欄はこの画面で直した欄だけを最新の上に重ねる
     * （見積の読み直しの rebaseEdits と同じ考え方）。重ねた欄があれば送る
     */
    synced: (e) => {
      const theirs = fromServer(e as Invoice);
      const base = headBase.current;
      const mine = latest.current;
      const merged = { ...theirs };
      for (const k of Object.keys(theirs) as Array<keyof Head>) {
        if (mine[k] !== base[k]) (merged as Record<keyof Head, unknown>)[k] = mine[k];
      }
      ver.current = e.updatedAt;
      headBase.current = theirs;
      latest.current = merged;
      setHead(merged);
      staleRef.current = false;
      if (JSON.stringify(merged) !== JSON.stringify(theirs)) hq.mark(true);
      else {
        hq.discard();
        hq.setState((x) => (x.kind === "error" ? { kind: "idle" } : x)); // 「ほかで変更があります」を消す（送る物は無い）
      }
    },
    register,
    actions: null,
    // #245（docs/94 B-4 案B）：頭は DocHead の3段。2段目＝請求書の絵と名前・件名・状態・？｜合計。3段目＝宛先・請求日・No.・お支払期限・{subject}（見積）
    docHead: {
      kind: "invoices",
      kindLabel: "請求書",
      title: (
        <input id="subj" className="dh-subj" style={subjWidth(head.subject)} aria-label="件名" placeholder="件名" value={head.subject} readOnly={readOnly} onChange={(e) => setH({ subject: e.target.value })} onKeyDown={(e) => e.key === "Enter" && e.preventDefault()} data-testid="invoice-subject" />
      ),
      primary: (
        <>
          <span className="hd-acts" data-testid="head-actions">
            <HeadActions actions={headActs} label={INVOICE_MSG.stateMenu} part="main" />
          </span>
          {readOnly ? (
            <Link className="btn primary" to={pdfUrl} data-testid="pdf-link">PDF 表示</Link>
          ) : (
            <button className="btn primary" type="button" onClick={() => void openIssue()} data-testid="issue-invoice">
              <Icon k="lock" />
              請求書発行
            </button>
          )}
        </>
      ),
      menu: <HeadActions actions={headActs} label={INVOICE_MSG.stateMenu} part="more" />,
      meta: (
        <>
          <DocField label="宛先">
            {/* #315：画面は「略称 ／ 支店 ／ 担当者」（敬称なし）。乗せると紙の宛名（正式名称・敬称つき）。発行前の確認の窓は紙の宛名のまま */}
            <span className="dh-to" title={rcp.paper} data-testid="doc-client">
              <b>{rcp.name}</b>
              {rcp.branch ? <span className="brn"> ／ {rcp.branch}</span> : null}
              {rcp.contact ? <span className="brn"> ／ {rcp.contact}</span> : null}
            </span>
          </DocField>
          <DocField label="請求日">
            <DateInput inline label="請求日" ariaLabel="請求日" value={head.issueDate} readOnly={readOnly} clearable={false} onChange={(v) => v && setH({ issueDate: v }, true)} testId="issue-date" />
          </DocField>
          {/* T2：取引日（1年のうちに使っている区分の税率が変わる・取引日が入っているときだけ。空＝請求日で計算） */}
          {initial.taxDateHint ? (
            <DocField label={<label htmlFor="h_taxPointDate">{TAX_MSG.taxDate}<Help label={TAX_MSG.taxDate} text={TAX_MSG.taxDateHelp} testId="help-tax-point-date" /></label>}>
              <DateInput inline id="h_taxPointDate" label={TAX_MSG.taxDate} ariaLabel={TAX_MSG.taxDate} value={head.taxPointDate ?? ""} readOnly={readOnly} onChange={(v) => setH({ taxPointDate: v || null }, true)} testId="tax-point-date" />
            </DocField>
          ) : null}
          <DocField label="No.">
            <span className="num">{initial.documentNo ?? "未発行"}</span>
          </DocField>
          <DocField label={<label htmlFor="dueDate">お支払期限</label>}>
            <DateInput id="dueDate" inline label="お支払期限" ariaLabel="お支払期限" value={head.dueDate} readOnly={readOnly} invalid={!head.dueDate} className="due" onChange={(v) => setH({ dueDate: v }, true)} testId="due-date" />
          </DocField>
          {initial.closing ? (
            // 締め請求書（#267）：{project}・受注をまたぐので工事の欄の代わりに「締め ○月分 ›」（締めの画面へ）
            <DocField label={CLOSING_MSG.band}>
              <Link to={`/closing?month=${initial.closing.periodEnd.slice(0, 7)}`} data-testid="invoice-closing-link">
                {CLOSING_MSG.monthLink(Number(initial.closing.periodEnd.slice(5, 7)))}
              </Link>
            </DocField>
          ) : initial.recurring ? (
            // 定期請求（#268）：工事の欄の代わりに契約（{project}のタブ「定期請求」へ）
            <DocField label={RECURRING_MSG.title}>
              <Link to={`/groups/${initial.recurring.groupId}?tab=recurring`} data-testid="invoice-recurring-link">
                {RECURRING_MSG.invoiceBand(initial.recurring.month, initial.recurring.groupName)} ›
              </Link>
            </DocField>
          ) : (
            <DocField label={L.subject}>
              <span className="muted">{initial.project.name || `（${L.subject}なし）`}{initial.project.quoteNo ? `　見積 ${initial.project.quoteNo}` : ""}</span>
            </DocField>
          )}
        </>
      ),
      tools: (
        <>
          {/* 取り込んだ請求書の元の PDF（#268 1.5c-1）：紙は作らない＝前のシステムの PDF を添付すると「PDF」で出る。ファイルの選択は label（Safari は隠した input の click で開かない） */}
          {initial.imported ? (
            <label className="btn sm quiet" data-testid="invoice-original-pdf-btn">
              {initial.imported.pdf ? IMPORT_MSG.pdfReplace : IMPORT_MSG.pdfAttach}
              <input type="file" accept="application/pdf,.pdf" className="vh" onChange={(e) => void attachOriginal(e.currentTarget)} data-testid="invoice-original-pdf" />
            </label>
          ) : null}
          {/* #138：見積書の頭の帯と同じ「詳細 ⌄」（縁付きの小さなボタン・開いているあいだは is-open） */}
          <button type="button" className={"btn sm quiet detailbtn" + (more ? " is-open" : "")} onClick={() => setMore((v) => !v)} title={more ? "閉じる" : `${L.site}・${L.period}`} aria-expanded={more} data-testid="head-more">
            {ESTIMATE_PC_MSG.more}
            <Icon k="chev" />
          </button>
        </>
      ),
    },
    /* 見出しの右（#171、docs/10 §5.12）：前は題の下の帯（出したあと・下書き・入金）。直後の一言は通知、状態はチップ、帯の中のボタンはチップの右 */
    chips: readOnly ? (
      <>
        <span className="chip ai" data-testid="invoice-status" title={`${initial.documentNo ?? ""} として${initial.issued ? md(initial.issued.at.slice(0, 10)) + "に" : ""}発行しました。発行した紙はここでは書き換えません`}>
          発行済{initial.issued ? ` ${mdSlash(dayJst(initial.issued.at))}` : ""}
        </span>
        {/* 分割の回（#172：見積の「分割 n／m回 請求済」にそろえて短く。回の名前まで入れた全文は title。見出しを 1280px で1段に） */}
        {inst ? (
          <span className="chip quiet" data-testid="invoice-installment" title={`${inst.count}回のうち${inst.seq}回目（${inst.label}）`}>
            分割 {inst.seq}／{inst.count}回目
          </span>
        ) : null}
        {notesChip}
        {/* 法令の文（登録番号が無い＝適格請求書でない）も文字のチップ。設定の話は「？」 */}
        {/* 税を{deliveryNote}で確定する請求書（#266）は{deliveryNote}が適格請求書＝このチップは出さない */}
        {/* 取り込んだ請求書（#268 1.5c-1）：前のシステムで発行したもの。適格請求書のチップは出さない（前のシステムの紙が正） */}
        {initial.imported ? (
          <span className="chip quiet" data-testid="invoice-imported" title={IMPORT_MSG.importedHelp}>
            {IMPORT_MSG.imported}
          </span>
        ) : null}
        {initial.issued && !initial.issued.isQualified && !fromNotes?.taxFromNotes && !initial.imported ? (
          <span className="chip shu" data-testid="invoice-not-qualified" title="登録番号が無いので、適格請求書（インボイス）ではありません">
            適格請求書ではありません
          </span>
        ) : null}
        {/* 部品の差し込み口（入金の状態のチップ。sales の会社の出した請求書だけ＝営業力と、2026-09-29 からは現場力も） */}
        {me.data ? <Slot name="invoice.head" me={me.data} invoice={initial} /> : null}
        {/* #223 の2回目：送付の記録（0回なら出さない） */}
        <DeliveriesLink filter={{ document: initial.id }} testId="invoice-deliveries" />
      </>
    ) : (
      <>
        {/* #276・#278：発行を取消して下書きに戻した請求書（番号あり）も「下書き」（一覧と同じ）。番号ありは乗せたときの説明と「？」で */}
        <span className="chip quiet" data-testid="invoice-status" title={initial.documentNo ? unissuedTitle(initial.documentNo) : undefined}>
          {initial.documentNo ? UNISSUED_MSG.chip : "下書き"}
        </span>
        <EditingChip editors={editors} />
        {inst ? (
          <span className="chip quiet" data-testid="invoice-installment" title={`${inst.count}回のうち${inst.seq}回目（${inst.label}）${inst.seq < inst.count ? "" : "。これが最後の回です"}`}>
            分割 {inst.seq}／{inst.count}回目
          </span>
        ) : notesChip ? (
          notesChip
        ) : !initial.documentNo ? (
          <span className="chip quiet" data-testid="invoice-installment" title={initial.project.quoteNo ? `見積 ${initial.project.quoteNo} の明細を写しました` : undefined}>
            一括{initial.project.invoiceCount > 0 ? `・この見積の ${initial.project.invoiceCount + 1} 枚目` : ""}
          </span>
        ) : null}
      </>
    ),
    /* 帯の使い方・仕組みの説明は題の右の「？」へ（#92。エディタが「自動で保存」と一緒に1つにまとめる） */
    help: readOnly
      ? [
          justIssued ? `右上の「PDF 表示」で${L.customer}に渡す紙になります。` : null,
          inst ? "紙の上の欄の数字は、出した時点で固めました。" : null,
          initial.issued && !initial.issued.isQualified && !fromNotes?.taxFromNotes ? "設定で登録番号を入れると次から印字されます。" : null,
          initial.canUnissue ? `修正するときは「取消し」で下書きに戻します（番号 ${initial.documentNo} はそのままです）。` : "修正するときは「訂正版」として新しく出します（原本は残ります）。",
        ]
      : [
          // #276：見出しのチップ（番号ありの下書き）の title と同じ1行＋発行の案内（見積書の #273 と同じ形）
          unissued || initial.documentNo ? `${unissuedTitle(unissued ?? initial.documentNo ?? "")}。` : null,
          unissued || initial.documentNo ? `「請求書発行」を押すと、同じ番号 ${unissued ?? initial.documentNo} で発行します。` : null,
          !initial.documentNo && inst ? "明細は1行で、金額は分割の予定から入れてあります。" : null,
          !initial.documentNo && inst && inst.seq < inst.count ? `これを出すと、${inst.seq + 1}回目をつくれます。` : null,
          !initial.documentNo && !inst && initial.project.invoiceCount > 0 ? "追加分なら、要らない行を消してください。" : null,
          !initial.documentNo && !inst && initial.project.invoiceCount === 0 && fresh ? "このまま出せます。出来高・追加分・値引きがあれば、行を押して修正できます。" : null,
          `支払条件は${termsOwner}の画面で変えられます。`,
        ],
    head: ({ total }) => (
      <div className="paper head ihead">
        {/* 締めの帯（#267、docs/97 決めごと6）：期間・締め日・束ねた書類の数｜明細の形 */}
        {initial.closing ? (
          <div className="cl-band" data-testid="invoice-closing-band">
            <span className="muted">{CLOSING_MSG.band}</span>
            <b>{closingPeriodLabel(initial.closing.periodStart, initial.closing.periodEnd)}</b>
            <span className="muted">{closingDayLabel(initial.closing.closingDay)}締め</span>
            <span>{t(CLOSING_MSG.bandDocs(initial.closing.notes, initial.closing.acceptances))}</span>
            <span className="sp" />
            <span className="muted">{CLOSING_MSG.mode}</span>
            <b>{BUNDLE_MODE_LABEL[initial.closing.mode]}</b>
          </div>
        ) : null}
        {/* 定期請求の帯（#268、Figma 283:545 ヘ）：「2026年11月分・営業力 利用契約 ›」＝契約へ戻る */}
        {initial.recurring ? (
          <div className="cl-band" data-testid="invoice-recurring-band">
            <span className="muted">{RECURRING_MSG.title}</span>
            <Link to={`/groups/${initial.recurring.groupId}?tab=recurring`} data-testid="invoice-recurring-band-link">
              <b>{RECURRING_MSG.invoiceBand(initial.recurring.month, initial.recurring.groupName)} ›</b>
            </Link>
          </div>
        ) : null}
        <div className="h2">
          <span className="muted">{termsOwner}の支払条件：{initial.paymentTermsLabel}</span>
          {!head.dueDate ? <span className="shu">お支払期限が空です。出すときに要ります</span> : null}
        </div>
        {/* 振込先（043、docs/32）：出したあとは固めた値。下書きで口座が2つ以上ならセレクト、1つなら文字、無ければ案内 */}
        <div className="h2" data-testid="bank-row">
          <label htmlFor="bankAccount">振込先：</label>
          {readOnly ? (
            <span data-testid="bank-text">{bankNow || "（振込先なし）"}</span>
          ) : accounts.length >= 2 ? (
            <select id="bankAccount" className="inp" value={head.bankAccountId ?? ""} onChange={(e) => setH({ bankAccountId: e.target.value || null }, true)} style={{ width: "auto", maxWidth: 560, minHeight: 40 }}>
              {head.bankAccountId == null || !accounts.some((a) => a.id === head.bankAccountId) ? <option value="">既定の口座（{bankAccountText(accounts.find((a) => a.isDefault))}）</option> : null}
              {accounts.map((a) => (
                <option key={a.id} value={a.id}>
                  {bankAccountText(a)}{a.isDefault ? "（既定）" : ""}
                </option>
              ))}
            </select>
          ) : accounts.length === 1 ? (
            <span data-testid="bank-text">{bankAccountText(accounts[0])}</span>
          ) : accountsQ.isPending ? (
            <span className="muted">…</span>
          ) : (
            <span className="muted" data-testid="bank-text">
              未登録（空のままでも出せます）
              {admin ? <Link className="btn text" style={{ marginLeft: 8 }} to="/settings/bank-accounts">振込口座登録</Link> : null}
            </span>
          )}
        </div>
        {inst ? (
          <div className="contract" aria-label="ご契約金額と、これまで・今回のご請求" data-testid="invoice-contract">
            <div><span>ご契約金額</span><b className="num">{yen(inst.contract)}</b></div>
            <div><span>これまでのご請求</span><b className="num">{yen(inst.before)}</b></div>
            <div className="now"><span>今回のご請求</span><b className="num">{yen(inst.now)}</b></div>
            <div><span>残り</span><b className="num">{yen(inst.rest)}</b></div>
          </div>
        ) : null}
        {/* 分割払いの「お支払い予定」（#267 3b、Figma 279:115）：出した請求書で sales の会社だけ。部品 payments が差し込む */}
        {readOnly && me.data ? <Slot name="invoice.schedule" me={me.data} invoice={initial} /> : null}
        {more ? (
          <div className="more" style={{ gridTemplateColumns: "2fr 1fr 1fr" }}>
            <div className="field">
              <label htmlFor="h_siteAddress">{L.site}</label>
              <input id="h_siteAddress" className="inp" value={head.siteAddress} readOnly={readOnly} onChange={(e) => setH({ siteAddress: e.target.value })} />
            </div>
            <div className="field">
              <label htmlFor="h_workPeriodText">{L.period}</label>
              <input id="h_workPeriodText" className="inp" value={head.workPeriodText} readOnly={readOnly} onChange={(e) => setH({ workPeriodText: e.target.value })} />
            </div>
            <div className="field">
              <label htmlFor="h_managementNo">
                {NUMBERING_MSG.managementNo}
                <Help label={NUMBERING_MSG.managementNo} text={NUMBERING_MSG.managementNoHelp} testId="help-management-no" />
              </label>
              <input id="h_managementNo" className="inp num" maxLength={50} value={mgmt} onChange={(e) => setMgmt(e.target.value)} onBlur={() => void saveMgmt()} onKeyDown={(e) => e.key === "Enter" && !e.nativeEvent.isComposing && (e.currentTarget as HTMLInputElement).blur()} data-testid="management-no" />
            </div>
          </div>
        ) : null}
        <ErrorText>{headErr}</ErrorText>
      </div>
    ),
    footer: (
      <>
        <div className="paper memo">
          <div className="field">
            <label htmlFor="memo">備考（紙の下に印字されます）</label>
            <textarea id="memo" value={head.notes} readOnly={readOnly} onChange={(e) => setH({ notes: e.target.value })} />
          </div>
        </div>
        {/* #302（B1）：紙の下の「PDF で確認」「下書き削除」の行はやめた（頭の帯の「PDF 確認」と「⋯」の「下書き削除」） */}
        {askDelete ? <DeleteDraftDialog prefix="invoice" onClose={() => setAskDelete(false)} onConfirm={deleteDraft} /> : null}
      </>
    ),
  };

  return (
    <>
      <Editor initial={initial} invoice={backend} />
      {hq.dialog}
      {issueDlg.open ? (
        <div className="dim" onClick={(e) => e.target === e.currentTarget && !issueDlg.busy && setIssueDlg((d) => ({ ...d, open: false }))}>
          <div className="dlg" role="dialog" aria-label="請求書発行" onKeyDown={(e) => e.key === "Escape" && !issueDlg.busy && setIssueDlg((d) => ({ ...d, open: false }))}>
            <div className="dh">
              発行前の確認
              <Help
                testId="help-issue"
                label="確かめの説明"
                text={helpText([
                  initial.documentNo ? null : "番号の書式は「設定 → 帳票番号」で変えられます。",
                  "仕入れ・利益率は印刷されません。登録番号（設定）があれば適格請求書として印字されます。",
                  needsCompany ? "会社名は紙に印字されるので要ります。住所・電話・振込先は空のままでも発行できます。次からは聞きません。" : null,
                  !bankNow && admin ? "振込先を印字するには「設定 → 振込口座」で口座を登録してから、戻って修正してください。" : null,
                ])}
              />
            </div>
            <div className="db">
              <div className="pv">
                <div><span className="muted">{L.customer}</span><b>{initial.client.name}{initial.client.branch ? ` ${initial.client.branch.name}` : ""} {toWhom}</b></div>
                <div><span className="muted">合計（税込）</span><b className="num">{yen((qc.getQueryData(["invoice", initial.id]) as Invoice | undefined)?.revision.total ?? initial.revision.total)} 円</b></div>
                <div><span className="muted">お支払期限</span><b className="num">{head.dueDate ? md(head.dueDate) : <span className="shu">（未入力）</span>}</b></div>
                {initial.documentNo ? (
                  <div><span className="muted">番号</span><b className="num">{initial.documentNo}</b></div>
                ) : manualNumber ? null : (
                  <div><span className="muted">番号</span><b className="num">{initial.numbering ? initial.numbering.next : "発行時に付きます"}</b>{initial.numbering ? <span className="muted">発行時に付きます</span> : null}</div>
                )}
                <div><span className="muted">件名</span><b>{head.subject || <span className="shu">（未入力）</span>}</b></div>
              </div>
              {/* #278：番号のある下書き（発行を取消した）は、同じ番号で再発行することを本文の1行で */}
              {initial.documentNo ? <p style={{ margin: 0 }} data-testid="issue-reissue">{unissuedReissue(initial.documentNo)}</p> : null}
              {manualNumber && initial.numbering ? (
                <Field id="is_no" label={NUMBERING_MSG.manualLabel} hint={NUMBERING_MSG.manualAuto(initial.numbering.next)}>
                  <input id="is_no" className="inp num" maxLength={50} autoComplete="off" value={issueDlg.manualNo ?? ""} onChange={(e) => setIssueDlg((d) => ({ ...d, manualNo: e.target.value, error: undefined }))} data-testid="issue-manual-no" />
                </Field>
              ) : null}
              {needsCompany ? (
                <div style={{ borderTop: "1px solid var(--rule)", paddingTop: 14 }}>
                  <p style={{ margin: "0 0 4px" }}>
                    <b>はじめての発行なので、あと少しだけ。</b>
                  </p>
                  <Field id="is_co" label="会社名（屋号・お名前でも）" style={{ marginBottom: 10 }}>
                    <input id="is_co" className="inp" autoFocus autoComplete="organization" value={issueDlg.company.name} onChange={(e) => setIssueDlg((d) => ({ ...d, company: { ...d.company, name: e.target.value }, error: undefined }))} />
                  </Field>
                  <div className="row" style={{ alignItems: "flex-end", marginBottom: 10 }}>
                    <Field id="is_addr" label="住所（請求書の右上に印字）" style={{ flex: "2 1 280px" }}>
                      <input id="is_addr" className="inp" value={issueDlg.company.address} onChange={(e) => setIssueDlg((d) => ({ ...d, company: { ...d.company, address: e.target.value } }))} />
                    </Field>
                    <div style={{ flex: "1 1 250px" }}>
                      <PhoneFields id="is_tel" label="電話番号" value={issueDlg.company.phone} onChange={(phone) => setIssueDlg((d) => ({ ...d, company: { ...d.company, phone } }))} />
                    </div>
                  </div>
                </div>
              ) : null}
              {/* 分割払い（#267 3b、Figma 279:53）：sales の会社だけ。部品 payments が差し込む */}
              {me.data ? <Slot name="invoice.issue" me={me.data} invoice={initial} value={instChoice} onChange={setInstChoice} /> : null}
              <div className="pv" data-testid="issue-bank">
                <div>
                  <span className="muted">振込先</span>
                  {bankNow ? <b>{bankNow}</b> : <b className="muted">（なし。空のままでも発行できます）</b>}
                </div>
              </div>
              <ErrorText>{issueDlg.error}</ErrorText>
              <p className="hint" style={{ margin: 0 }}>発行したあとは、この紙は書き換えられません。修正するときは「取消し」で下書きに戻して再発行します（番号はそのまま）。</p>
            </div>
            <div className="df">
              <button type="button" className="btn quiet" disabled={issueDlg.busy} onClick={() => setIssueDlg((d) => ({ ...d, open: false }))}>戻って修正</button>
              <button type="button" className="btn primary big" disabled={issueDlg.busy} onClick={() => void doIssue()} data-testid="issue-confirm">
                <Icon k="lock" />
                この内容で発行
              </button>
            </div>
          </div>
        </div>
      ) : null}
      {unissue.open ? (
        // 取消しの窓（#188。見積書・領収書と共用）：題に警告のしるし＋1文。説明は「？」
        <UnissueDialog doc="invoice" no={initial.documentNo ?? ""} busy={unissue.busy} error={unissue.error} onClose={() => setUnissue((u) => ({ ...u, open: false }))} onConfirm={() => void doUnissue()} />
      ) : null}
      {toastNode}
    </>
  );
}
